<template>
    <main v-if="!$auth.loading && hasToken" class="rp-page">
        <section class="rp-brand" aria-label="Data Heroes">
            <div class="rp-brand-inner">
                <img class="rp-brand-logo" :src="logoSrc" alt="Data Heroes" />
                <div class="rp-brand-copy">
                    <div class="rp-eyebrow">
                        <span class="rp-eyebrow-dot" aria-hidden="true"></span>
                        Безопасное восстановление доступа
                    </div>
                    <h1>
                        Создайте новый пароль<br />
                        <span>и продолжайте работу</span>
                    </h1>
                    <p>
                        Ссылка из письма подтверждает, что это действительно Вы. После сохранения нового пароля
                        вход в систему выполнится автоматически.
                    </p>
                </div>
                <div class="rp-steps" aria-label="Этапы восстановления">
                    <div class="rp-step">
                        <b>1</b>
                        <span>Ссылка<br />из письма</span>
                    </div>
                    <div class="rp-step is-active">
                        <b>2</b>
                        <span>Новый<br />пароль</span>
                    </div>
                    <div class="rp-step">
                        <b>3</b>
                        <span>Вход<br />в систему</span>
                    </div>
                </div>
            </div>
        </section>

        <section class="rp-main">
            <article class="rp-card" aria-live="polite">
                <div v-if="view === 'checking'" class="rp-checking-view">
                    <div class="rp-checking-spinner" aria-hidden="true"></div>
                    <h2>Проверяем ссылку</h2>
                    <p>Убеждаемся, что ссылка из письма ещё действительна.</p>
                </div>

                <div v-else-if="view === 'form'" class="rp-form-view">
                    <header class="rp-card-head">
                        <h2>Придумайте новый пароль</h2>
                        <p>Он будет использоваться для входа в личный кабинет Data Heroes.</p>
                    </header>
                    <form class="rp-card-body" novalidate @submit.prevent="submit">
                        <div class="rp-field-group">
                            <label class="rp-field-label" for="rp-password">Новый пароль</label>
                            <div class="rp-password-field" :class="{ 'is-invalid': fieldErrors.password }">
                                <input
                                    id="rp-password"
                                    v-model="password"
                                    :type="showPassword ? 'text' : 'password'"
                                    autocomplete="new-password"
                                    placeholder="Введите новый пароль"
                                    required
                                    @input="onPasswordInput('password', $event)"
                                />
                                <button
                                    type="button"
                                    class="rp-eye"
                                    :aria-label="showPassword ? 'Скрыть пароль' : 'Показать пароль'"
                                    @click="showPassword = !showPassword"
                                >
                                    <v-icon size="20">{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                                </button>
                            </div>
                            <div v-if="fieldErrors.password" class="rp-field-error">{{ fieldErrors.password }}</div>
                        </div>

                        <div class="rp-field-group">
                            <label class="rp-field-label" for="rp-confirm">Повторите пароль</label>
                            <div class="rp-password-field" :class="{ 'is-invalid': fieldErrors.confirm }">
                                <input
                                    id="rp-confirm"
                                    v-model="confirm"
                                    :type="showConfirm ? 'text' : 'password'"
                                    autocomplete="new-password"
                                    placeholder="Введите пароль ещё раз"
                                    required
                                    @input="onPasswordInput('confirm', $event)"
                                />
                                <button
                                    type="button"
                                    class="rp-eye"
                                    :aria-label="showConfirm ? 'Скрыть пароль' : 'Показать пароль'"
                                    @click="showConfirm = !showConfirm"
                                >
                                    <v-icon size="20">{{ showConfirm ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                                </button>
                            </div>
                            <div v-if="fieldErrors.confirm" class="rp-field-error">{{ fieldErrors.confirm }}</div>
                        </div>

                        <div class="rp-requirements" aria-label="Требования к паролю">
                            <div class="rp-requirements-title">Пароль должен содержать:</div>
                            <ul class="rp-requirements-list">
                                <li :class="{ 'is-ok': checks.length }"><i>✓</i>Не менее 8 символов</li>
                                <li :class="{ 'is-ok': checks.letter }"><i>✓</i>Любую букву</li>
                                <li :class="{ 'is-ok': checks.number }"><i>✓</i>Цифру</li>
                                <li :class="{ 'is-ok': checks.match }"><i>✓</i>Пароли совпадают</li>
                            </ul>
                        </div>

                        <button class="rp-primary" type="submit" :disabled="loading">
                            {{ loading ? 'Сохраняем…' : 'Сохранить пароль и войти' }}
                        </button>
                        <p class="rp-footnote">После сохранения Вы автоматически войдёте в личный кабинет.</p>
                    </form>
                </div>

                <div v-else-if="view === 'success'" class="rp-success-view">
                    <div class="rp-success-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none">
                            <path
                                d="m5 12.5 4.2 4.2L19 7"
                                stroke="currentColor"
                                stroke-width="2.4"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </div>
                    <h2>Пароль обновлён</h2>
                    <p>Доступ восстановлен. Выполняем вход в систему Data Heroes.</p>
                    <div class="rp-progress"><span></span></div>
                    <div class="rp-redirect-note">
                        Переход через <b>{{ countdown }}</b> сек.
                    </div>
                    <button class="rp-secondary" type="button" @click="goToProjects">
                        Перейти в систему сейчас
                    </button>
                </div>

                <div v-else-if="view === 'checkError'" class="rp-token-error">
                    <div class="rp-token-error-icon" aria-hidden="true">!</div>
                    <h2>Не удалось проверить ссылку</h2>
                    <p>{{ checkErrorMessage }}</p>
                    <button class="rp-primary" type="button" @click="reloadPage">Перезагрузить страницу</button>
                    <button class="rp-secondary" type="button" @click="goToLanding">На главную</button>
                </div>

                <div v-else class="rp-token-error">
                    <div class="rp-token-error-icon" aria-hidden="true">!</div>
                    <h2>Ссылка недействительна</h2>
                    <p>{{ tokenErrorMessage }}</p>
                    <button class="rp-primary" type="button" @click="goToResetRequest">
                        Запросить восстановление заново
                    </button>
                    <button class="rp-secondary" type="button" @click="goToLanding">На главную</button>
                </div>
            </article>
        </section>
    </main>
</template>

<script>
const LETTER_RE = /[A-Za-zА-Яа-яЁё]/
const NUMBER_RE = /\d/
const TOKEN_ERROR_MESSAGES = [
    'Недействительная ссылка',
    'Ссылка недействительна или устарела. Запросите восстановление пароля заново.',
]
const CHECK_ERROR_MESSAGE = 'Произошла ошибка, перезагрузите страницу'

export default {
    name: 'ResetPassword',
    data: () => ({
        logoSrc: require('@/assets/Logo_light.svg'),
        password: '',
        confirm: '',
        showPassword: false,
        showConfirm: false,
        fieldErrors: {},
        loading: false,
        view: 'checking',
        tokenErrorMessage: TOKEN_ERROR_MESSAGES[1],
        checkErrorMessage: CHECK_ERROR_MESSAGE,
        countdown: 3,
        redirectTimer: null,
        hasToken: false,
    }),
    computed: {
        resetToken() {
            const token = this.$route.query.token
            return typeof token === 'string' ? token.trim() : ''
        },
        checks() {
            const value = this.password
            return {
                length: value.length >= 8,
                letter: LETTER_RE.test(value),
                number: NUMBER_RE.test(value),
                match: value.length > 0 && value === this.confirm,
            }
        },
    },
    created() {
        if (!this.resetToken) {
            this.$router.replace('/landing')
            return
        }
        this.hasToken = true
        this.verifyToken()
    },
    beforeDestroy() {
        this.clearRedirectTimer()
    },
    methods: {
        async verifyToken() {
            this.view = 'checking'
            try {
                const { error, data } = await this.$auth.checkResetToken(this.resetToken)
                if (error) {
                    this.checkErrorMessage = CHECK_ERROR_MESSAGE
                    this.view = 'checkError'
                    return
                }
                if (!data?.valid) {
                    this.tokenErrorMessage = TOKEN_ERROR_MESSAGES[1]
                    this.view = 'tokenError'
                    return
                }
                this.view = 'form'
            } catch (e) {
                this.checkErrorMessage = CHECK_ERROR_MESSAGE
                this.view = 'checkError'
            }
        },
        stripSpaces(value) {
            return String(value || '').replace(/\s/g, '')
        },
        onPasswordInput(field, event) {
            const cleaned = this.stripSpaces(event.target.value)
            if (field === 'password') {
                this.password = cleaned
                if (this.fieldErrors.password) {
                    const next = { ...this.fieldErrors }
                    delete next.password
                    this.fieldErrors = next
                }
            } else {
                this.confirm = cleaned
                if (this.fieldErrors.confirm) {
                    const next = { ...this.fieldErrors }
                    delete next.confirm
                    this.fieldErrors = next
                }
            }
            if (event.target.value !== cleaned) {
                event.target.value = cleaned
            }
        },
        validate() {
            const errors = {}
            const { length, letter, number, match } = this.checks
            if (!length || !letter || !number) {
                errors.password = 'Пароль не соответствует требованиям.'
            }
            if (!match) {
                errors.confirm = 'Пароли не совпадают.'
            }
            this.fieldErrors = errors
            return Object.keys(errors).length === 0
        },
        isTokenError(message) {
            return TOKEN_ERROR_MESSAGES.includes(message)
        },
        async submit() {
            if (!this.validate() || this.loading) return

            this.loading = true
            const { error } = await this.$auth.setNewPassword(this.resetToken, this.password)
            this.loading = false

            if (error) {
                const message = error.message || 'Не удалось сохранить пароль.'
                if (this.isTokenError(message)) {
                    this.tokenErrorMessage = message
                    this.view = 'tokenError'
                    return
                }
                this.fieldErrors = { password: message }
                return
            }

            this.view = 'success'
            this.startRedirectCountdown()
        },
        startRedirectCountdown() {
            this.clearRedirectTimer()
            this.countdown = 3
            this.redirectTimer = setInterval(() => {
                this.countdown -= 1
                if (this.countdown <= 0) {
                    this.goToProjects()
                }
            }, 1000)
        },
        clearRedirectTimer() {
            if (this.redirectTimer) {
                clearInterval(this.redirectTimer)
                this.redirectTimer = null
            }
        },
        goToProjects() {
            this.clearRedirectTimer()
            this.$router.push('/projects')
        },
        goToLanding() {
            this.$router.push('/landing')
        },
        goToResetRequest() {
            this.$store.commit('setAuthDialogStartSection', 'passwordReset')
            this.$store.commit('setIsAuthDialog', true)
            this.$router.push('/landing')
        },
        reloadPage() {
            window.location.reload()
        },
    },
}
</script>

<style lang="sass">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap')
@import '@/vars.sass'

.rp-page
    --ink: #{$dark-new}
    --indigo: #{$main-new}
    --indigo-d: #454CC0
    --wasabi: #{$pop-new}
    --bg: #{$app-bcg-new}
    --surface: #fff
    --line: #{$border-new}
    --text: #{$text-color-new}
    --mut: #{$muted-new}
    --success: #16865d
    --success-bg: #e9f8f2
    --danger: #{$alert-new}
    --danger-bg: #fff0f3
    --shadow: 0 28px 70px rgba(25, 25, 46, .24)

    box-sizing: border-box
    width: 100%
    min-height: 100%
    height: 100%
    display: grid
    grid-template-columns: minmax(300px, 42%) minmax(0, 1fr)
    overflow: hidden
    font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif
    color: var(--text)
    -webkit-font-smoothing: antialiased
    background: var(--ink)

    *,
    *::before,
    *::after
        box-sizing: border-box

    .rp-brand
        position: relative
        padding: clamp(24px, 3.5vh, 40px) clamp(24px, 3vw, 48px)
        color: #fff !important
        background: radial-gradient(circle at 82% 18%, rgba(82, 90, 213, .52), transparent 30%), radial-gradient(circle at 12% 88%, rgba(212, 236, 56, .10), transparent 22%), #19192e
        overflow: hidden
        min-height: 0

        &::before
            content: ''
            position: absolute
            inset: 0
            background-image: radial-gradient(rgba(255, 255, 255, .075) 1px, transparent 1px)
            background-size: 22px 22px
            opacity: .65

        &::after
            content: ''
            position: absolute
            width: 440px
            height: 440px
            right: -170px
            top: 80px
            border: 1px solid rgba(212, 236, 56, .20)
            border-radius: 50%
            box-shadow: 0 0 0 44px rgba(82, 90, 213, .08), 0 0 0 88px rgba(82, 90, 213, .04)

        h1
            margin: 20px 0 0
            font-family: 'Geologica', sans-serif
            font-size: clamp(28px, 3.6vw, 56px)
            line-height: 1.05
            letter-spacing: -.045em
            font-weight: 900
            color: #fff !important

            span
                color: #{$pop-new} !important

        p
            margin: 16px 0 0
            max-width: 43ch
            color: rgba(255, 255, 255, .67) !important
            font-size: clamp(14px, 1.2vw, 16px)
            line-height: 1.55

    .rp-brand-inner
        position: relative
        z-index: 2
        height: 100%
        min-height: 0
        display: flex
        flex-direction: column
        gap: clamp(16px, 2.5vh, 28px)

    .rp-brand-logo
        display: block
        width: min(180px, 42%)
        height: auto
        object-fit: contain
        flex: 0 0 auto

    .rp-brand-copy
        margin: auto 0
        max-width: 520px

    .rp-eyebrow
        display: inline-flex
        align-items: center
        gap: 8px
        color: #{$pop-new} !important
        font-family: 'Geologica', sans-serif
        font-size: 12px
        font-weight: 800
        letter-spacing: .12em
        text-transform: uppercase

    .rp-eyebrow-dot
        width: 8px
        height: 8px
        border-radius: 50%
        background: var(--wasabi)
        box-shadow: 0 0 0 5px rgba(212, 236, 56, .13)

    .rp-steps
        display: flex
        gap: clamp(12px, 2vw, 24px)
        padding-top: clamp(14px, 2vh, 24px)
        border-top: 1px solid rgba(255, 255, 255, .12)
        flex: 0 0 auto

    .rp-step
        display: flex
        align-items: center
        gap: 10px
        color: rgba(255, 255, 255, .54) !important
        font-size: 12px
        line-height: 1.35

        b
            display: grid
            place-items: center
            width: 28px
            height: 28px
            border-radius: 9px
            background: rgba(255, 255, 255, .08)
            color: #fff !important
            font-family: 'Geologica', sans-serif
            flex: 0 0 auto

        span
            color: inherit !important

        &.is-active
            color: #fff !important

            b
                background: var(--wasabi)
                color: var(--ink) !important

    .rp-main
        position: relative
        display: grid
        place-items: center
        padding: clamp(16px, 3vh, 40px) clamp(16px, 3vw, 40px)
        background: var(--bg)
        overflow: auto
        min-height: 0
        min-width: 0

    .rp-card
        width: min(100%, 560px)
        max-height: 100%
        background: var(--surface)
        border: 1px solid rgba(255, 255, 255, .85)
        border-radius: 28px
        box-shadow: var(--shadow)
        overflow: auto

    .rp-card-head
        position: relative
        padding: clamp(22px, 3vh, 32px) clamp(20px, 3vw, 36px) clamp(18px, 2.5vh, 28px)
        color: #fff !important
        background: radial-gradient(circle at 88% 20%, rgba(212, 236, 56, .18), transparent 21%), linear-gradient(135deg, #202043, #2a2d68)

        &::before
            content: ''
            position: absolute
            inset: 0
            background: repeating-radial-gradient(ellipse at 80% 90%, transparent 0 28px, rgba(124, 131, 232, .12) 29px 30px)
            opacity: .7
            pointer-events: none

        > *
            position: relative
            z-index: 1

        h2
            margin: 0
            font-family: 'Geologica', sans-serif
            font-size: clamp(24px, 3vw, 32px)
            line-height: 1.12
            letter-spacing: -.035em
            font-weight: 900
            color: #fff !important

        p
            margin: 10px 0 0
            color: rgba(255, 255, 255, .66) !important
            font-size: 14px
            line-height: 1.55
            max-width: 48ch

    .rp-card-body
        padding: clamp(20px, 3vh, 32px) clamp(16px, 3vw, 36px) clamp(22px, 3vh, 36px)

    .rp-field-group + .rp-field-group
        margin-top: 18px

    .rp-field-group
        color: #1f2440 !important

    .rp-field-label
        display: flex
        justify-content: space-between
        gap: 12px
        margin-bottom: 8px
        color: #1f2440 !important
        font-size: 13px
        font-weight: 700 !important
        line-height: 1.2

    .rp-password-field
        position: relative

        input
            width: 100%
            height: 52px
            border: 1.5px solid var(--line)
            border-radius: 16px
            padding: 0 52px 0 16px
            color: var(--text) !important
            background: #fff
            outline: none
            transition: .18s
            font: inherit

            &::placeholder
                color: #adb4c4

            &:hover
                border-color: #cdd2df

            &:focus
                border-color: var(--indigo)
                box-shadow: 0 0 0 4px rgba(82, 90, 213, .12)

        &.is-invalid input
            border-color: var(--danger)
            box-shadow: 0 0 0 4px rgba(232, 24, 60, .09)

    .rp-eye
        position: absolute
        right: 10px
        top: 50%
        transform: translateY(-50%)
        width: 38px
        height: 38px
        border: 0
        border-radius: 11px
        background: transparent
        color: #7f8798 !important
        cursor: pointer
        display: grid
        place-items: center
        padding: 0

        &:hover
            background: #f1f2f7
            color: var(--indigo) !important

    .rp-field-error
        margin-top: 7px
        color: var(--danger) !important
        font-size: 12px
        line-height: 1.35
        font-weight: 600

    .rp-requirements
        margin-top: 18px
        padding: 14px 16px
        border: 1px solid var(--line)
        border-radius: 16px
        background: #fafbfe

    .rp-requirements-title
        font-size: 12px
        font-weight: 700
        color: var(--mut) !important
        margin-bottom: 10px

    .rp-requirements-list
        display: grid
        grid-template-columns: 1fr 1fr
        gap: 8px 14px
        list-style: none
        margin: 0
        padding: 0

        li
            display: flex
            align-items: center
            gap: 8px
            color: #8a91a3 !important
            font-size: 12px

            i
                width: 18px
                height: 18px
                border-radius: 50%
                display: grid
                place-items: center
                background: #eceef4
                font-style: normal
                font-size: 11px
                color: #949bad !important
                flex: 0 0 auto

            &.is-ok
                color: var(--success) !important

                i
                    background: var(--success-bg)
                    color: var(--success) !important

    .rp-primary
        width: 100%
        height: 52px
        margin-top: 22px
        border: 0
        border-radius: 15px
        background: var(--indigo)
        color: #fff !important
        font-family: 'Geologica', sans-serif
        font-weight: 800
        font-size: 15px
        cursor: pointer
        box-shadow: 0 10px 24px rgba(82, 90, 213, .28)
        transition: .18s

        &:hover:not(:disabled)
            background: var(--indigo-d)
            transform: translateY(-1px)

        &:disabled
            cursor: not-allowed
            opacity: .56
            box-shadow: none
            transform: none

    .rp-footnote
        margin: 14px 0 0
        text-align: center
        color: var(--mut) !important
        font-size: 11.5px
        line-height: 1.5

    .rp-success-view,
    .rp-token-error,
    .rp-checking-view
        text-align: center
        padding: clamp(28px, 5vh, 44px) clamp(20px, 3vw, 36px) clamp(28px, 4vh, 40px)

    .rp-checking-view h2
        margin: 22px 0 0
        font-family: 'Geologica', sans-serif
        font-size: clamp(24px, 3vw, 28px)
        font-weight: 900
        letter-spacing: -.03em
        color: var(--text) !important

    .rp-checking-view p
        margin: 10px auto 0
        max-width: 34ch
        color: var(--mut) !important
        font-size: 14px
        line-height: 1.55

    .rp-checking-spinner
        width: 56px
        height: 56px
        margin: 0 auto
        border-radius: 50%
        border: 3px solid rgba(82, 90, 213, .14)
        border-top-color: var(--indigo)
        animation: rp-spin .75s linear infinite
        box-shadow: 0 10px 26px rgba(82, 90, 213, .12)

    .rp-success-icon
        width: 72px
        height: 72px
        margin: 0 auto
        display: grid
        place-items: center
        border-radius: 22px
        background: var(--success-bg)
        color: var(--success)
        box-shadow: 0 10px 26px rgba(22, 134, 93, .12)

        svg
            width: 36px
            height: 36px

    .rp-success-view h2,
    .rp-token-error h2
        margin: 20px 0 0
        font-family: 'Geologica', sans-serif
        font-size: clamp(24px, 3vw, 28px)
        font-weight: 900
        letter-spacing: -.03em
        color: var(--text) !important

    .rp-success-view p,
    .rp-token-error p
        margin: 10px auto 0
        max-width: 38ch
        color: var(--mut) !important
        font-size: 14px
        line-height: 1.55

    .rp-progress
        height: 6px
        margin-top: 26px
        border-radius: 999px
        background: #eceef4
        overflow: hidden

        span
            display: block
            height: 100%
            width: 0
            background: var(--success)
            animation: rp-load 3s linear forwards

    .rp-redirect-note
        margin-top: 12px
        color: var(--mut) !important
        font-size: 12px

        b
            color: inherit !important

    .rp-secondary
        width: 100%
        height: 48px
        margin-top: 18px
        border: 1px solid var(--line)
        border-radius: 14px
        background: #fff
        color: var(--indigo) !important
        font-family: 'Geologica', sans-serif
        font-weight: 800
        cursor: pointer
        transition: .16s

        &:hover
            background: #f6f7ff
            border-color: #ccd0f5

    .rp-token-error-icon
        width: 72px
        height: 72px
        margin: 0 auto
        display: grid
        place-items: center
        border-radius: 22px
        background: var(--danger-bg)
        color: var(--danger) !important
        font-family: 'Geologica', sans-serif
        font-size: 32px
        font-weight: 900
        box-shadow: 0 10px 26px rgba(232, 24, 60, .1)

    .rp-token-error .rp-primary
        margin-top: 24px

@keyframes rp-load
    to
        width: 100%

@keyframes rp-spin
    to
        transform: rotate(360deg)

@media (max-width: 920px)
    .rp-page
        grid-template-columns: 1fr

        .rp-brand
            display: none

        .rp-main
            min-height: 100%
            padding: 20px 16px

@media (max-width: 560px)
    .rp-page
        .rp-main
            padding: 12px

        .rp-card
            border-radius: 22px

        .rp-requirements-list
            grid-template-columns: 1fr
</style>
